import React, { useState, useEffect } from 'react'
import { ModalBase } from '@/components/modals/ModalBase'
import { enrollmentService } from '@/services/enrollmentService'
import { paymentService } from '@/services/paymentService'
import { PaymentInstructions } from '@/components/payment/PaymentInstructions'
import { PaymentStatusPoller } from '@/components/payment/PaymentStatusPoller'
import type { PaymentTransaction, PaymentChannel } from '@/types/payment'
import { groupPaymentChannelsByMethod, formatPaymentMethodLabel } from '@/types/payment'
import { useAuth } from '@/hooks/useAuth'
import { getPaymentConfig } from '@/config/payment'
import { paymentProofService } from '@/services/paymentProofService'
import { FileUpload } from '@/components/common/FileUpload'

export interface BatchCheckoutModalProps {
  isOpen: boolean
  onClose: () => void
  batch: { id: string; title: string; batchFee: number; registrationDeadline?: string; endDate?: string }
  courseId: string
  courseName: string
  onEnrollmentCreated?: (enrollment: { id: string }) => void
}

type CheckoutStep = 'review' | 'select_payment' | 'processing' | 'payment_pending' | 'manual_transfer' | 'success' | 'error'

export const BatchCheckoutModal: React.FC<BatchCheckoutModalProps> = ({
  isOpen,
  onClose,
  batch,
  courseId,
  courseName,
  onEnrollmentCreated,
}) => {
  const { user } = useAuth()
  const [step, setStep] = useState<CheckoutStep>('review')
  const [payment, setPayment] = useState<PaymentTransaction | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [selectedChannel, setSelectedChannel] = useState<PaymentChannel | null>(null)

  const [bankConfig, setBankConfig] = useState<{ bankName: string; accountNumber: string; accountHolderName: string } | null>(null)
  const [manualEnrollment, setManualEnrollment] = useState<{ id: string } | null>(null)

  // Payment proof upload state
  const [proofBankName, setProofBankName] = useState('')
  const [proofAccountNumber, setProofAccountNumber] = useState('')
  const [proofAccountHolder, setProofAccountHolder] = useState('')
  const [proofFileUrl, setProofFileUrl] = useState('')
  const [proofNotes, setProofNotes] = useState('')
  const [proofError, setProofError] = useState<string | null>(null)
  const [isSubmittingProof, setIsSubmittingProof] = useState(false)
  const [proofSubmitted, setProofSubmitted] = useState(false)

  const paymentMethodGroups = groupPaymentChannelsByMethod()

  useEffect(() => {
    if (isOpen) {
      setStep('review')
      setPayment(null)
      setErrorMessage('')
      setSelectedChannel(null)
      setBankConfig(null)
      setManualEnrollment(null)
      setProofBankName('')
      setProofAccountNumber('')
      setProofAccountHolder('')
      setProofFileUrl('')
      setProofNotes('')
      setProofError(null)
      setIsSubmittingProof(false)
      setProofSubmitted(false)

      // Validate batch availability on open
      const now = new Date()
      if (batch.registrationDeadline && new Date(batch.registrationDeadline) < now) {
        setErrorMessage('Registrasi batch ini sudah ditutup. Silakan pilih batch lain.')
        setStep('error')
      } else if (batch.endDate && new Date(batch.endDate) < now) {
        setErrorMessage('Batch ini sudah selesai. Silakan pilih batch lain.')
        setStep('error')
      }
    }
  }, [isOpen, batch.registrationDeadline, batch.endDate])

  const handleCreatePayment = async () => {
    if (!selectedChannel) {
      setErrorMessage('Silakan pilih metode pembayaran')
      return
    }

    setStep('processing')
    setErrorMessage('')

    try {
      // Langkah 1: Daftarkan ke kursus dengan batch tertentu
      const enrollmentResponse = await enrollmentService.enrollInCourse(courseId, batch.id)

      if (!enrollmentResponse.data) {
        throw new Error('Data pendaftaran tidak tersedia')
      }

      const enrollmentId = enrollmentResponse.data.id

      // Langkah 2: Buat pembayaran untuk pendaftaran tersebut
      const paymentResult = await paymentService.createPayment({
        enrollmentId,
        paymentChannel: selectedChannel,
        customerName: user?.name || 'Peserta',
        customerEmail: user?.email || 'peserta@example.com',
      })

      setPayment(paymentResult)
      setStep('payment_pending')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Gagal memproses pembayaran'
      setErrorMessage(message)
      setStep('error')
    }
  }

  const handlePaymentSuccess = () => {
    setStep('success')
  }

  const handlePaymentFailed = () => {
    setErrorMessage('Pembayaran gagal. Silakan coba lagi.')
    setStep('error')
  }

  const handlePaymentExpired = () => {
    setErrorMessage('Pembayaran kedaluwarsa. Silakan coba lagi.')
    setStep('error')
  }

  const handleManualTransfer = async () => {
    setStep('processing')
    setErrorMessage('')

    try {
      let enrollmentData: { id: string }

      try {
        const enrollmentResponse = await enrollmentService.enrollInCourse(courseId, batch.id)

        if (!enrollmentResponse.data) {
          throw new Error('Gagal membuat pendaftaran')
        }

        enrollmentData = enrollmentResponse.data
      } catch (enrollError) {
        // Handle 409 Conflict — enrollment already exists (pending_payment)
        if (enrollError instanceof Error && enrollError.message.includes('already enrolled')) {
          const existingEnrollments = await enrollmentService.getUserEnrollments(user?.id || '', {})
          const existingEnrollment = existingEnrollments?.data?.find(
            (e: any) => e.courseId === courseId && e.batchId === batch.id && e.paymentStatus === 'pending'
          )
          if (existingEnrollment) {
            enrollmentData = existingEnrollment
          } else {
            throw enrollError
          }
        } else {
          throw enrollError
        }
      }

      setManualEnrollment(enrollmentData)
      onEnrollmentCreated?.(enrollmentData)

      // Load bank config
      const config = await getPaymentConfig()
      setBankConfig(config)

      setStep('manual_transfer')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Gagal memproses pendaftaran'
      setErrorMessage(message)
      setStep('error')
    }
  }

  const handleSubmitProof = async () => {
    if (!proofBankName.trim() || !proofAccountNumber.trim() || !proofAccountHolder.trim() || !proofFileUrl.trim()) {
      setProofError('Semua field wajib diisi')
      return
    }

    setIsSubmittingProof(true)
    setProofError(null)

    try {
      await paymentProofService.submitProof(manualEnrollment!.id, {
        amount_paid: batch.batchFee,
        bank_name: proofBankName.trim(),
        account_number: proofAccountNumber.trim(),
        account_holder_name: proofAccountHolder.trim(),
        proof_file_url: proofFileUrl.trim(),
        notes: proofNotes.trim() || undefined,
      })
      setProofSubmitted(true)
    } catch (err) {
      setProofError(err instanceof Error ? err.message : 'Gagal mengirim bukti pembayaran')
    } finally {
      setIsSubmittingProof(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  // Langkah: Sedang diproses
  if (step === 'processing') {
    return (
      <ModalBase isOpen={isOpen} onClose={() => {}}>
        <div className="flex flex-col items-center text-center py-12">
          <div className="size-16 border-4 border-primary/30 border-t-primary rounded-full animate-spin mb-6" />
          <h3 className="text-xl font-bold text-text-main dark:text-white mb-2">
            Sedang Diproses...
          </h3>
          <p className="text-gray-500 dark:text-text-muted">
            Mohon tunggu sementara kami memproses permintaan Anda
          </p>
        </div>
      </ModalBase>
    )
  }

  // Langkah: Error
  if (step === 'error') {
    return (
      <ModalBase isOpen={isOpen} onClose={onClose}>
        <div className="flex flex-col items-center text-center pt-8 pb-4">
          <div className="size-24 bg-red-100 dark:bg-red-500/20 rounded-full flex items-center justify-center mb-6 text-red-500">
            <span className="material-symbols-outlined text-[48px]">error</span>
          </div>
          <h3 className="text-2xl font-bold text-text-main dark:text-white mb-2">
            Terjadi Kesalahan
          </h3>
          <p className="text-gray-500 dark:text-text-muted mb-8">
            {errorMessage || 'Terjadi kesalahan yang tidak terduga. Silakan coba lagi.'}
          </p>
          <button
            onClick={() => setStep('review')}
            className="w-full py-4 bg-primary text-black font-bold rounded-full mb-3 shadow-lg active:scale-[0.98] transition-transform"
          >
            Coba Lagi
          </button>
          <button
            onClick={onClose}
            className="w-full py-3 text-gray-500 dark:text-text-muted font-medium"
          >
            Batal
          </button>
        </div>
      </ModalBase>
    )
  }

  // Langkah: Menunggu pembayaran
  if (step === 'payment_pending' && payment) {
    return (
      <ModalBase isOpen={isOpen} onClose={onClose}>
        <div className="pb-4">
          <h3 className="text-xl font-bold tracking-tight text-text-main dark:text-white mb-6">
            Selesaikan Pembayaran
          </h3>

          <PaymentStatusPoller
            transactionId={payment.id}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentFailed={handlePaymentFailed}
            onPaymentExpired={handlePaymentExpired}
          />

          <div className="mt-6">
            <PaymentInstructions
              payment={payment}
              onCopyCode={() => {}}
            />
          </div>

          <button
            onClick={onClose}
            className="w-full mt-6 py-3 text-gray-500 dark:text-text-muted font-medium"
          >
            Tutup (pembayaran akan terus berlanjut)
          </button>
        </div>
      </ModalBase>
    )
  }

  // Langkah: Transfer Manual + Upload Bukti
  if (step === 'manual_transfer' && manualEnrollment && bankConfig) {
    // Success state
    if (proofSubmitted) {
      return (
        <ModalBase isOpen={isOpen} onClose={onClose}>
          <div className="pb-4 text-center">
            <div className="size-20 bg-emerald-100 dark:bg-emerald-500/20 rounded-full flex items-center justify-center mb-6 mx-auto">
              <span className="material-symbols-outlined text-[40px] text-emerald-600 dark:text-emerald-400 filled">check_circle</span>
            </div>
            <h3 className="text-xl font-bold text-text-main dark:text-white mb-2">
              Bukti Pembayaran Terkirim!
            </h3>
            <p className="text-gray-500 dark:text-text-muted mb-6 text-sm">
              Bukti pembayaran Anda sedang diverifikasi oleh admin.
            </p>
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                <strong>Status:</strong> Menunggu Verifikasi
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-full py-4 bg-primary text-black font-bold rounded-full shadow-lg active:scale-[0.98] transition-transform"
            >
              Tutup
            </button>
          </div>
        </ModalBase>
      )
    }

    // Upload form state
    return (
      <ModalBase isOpen={isOpen} onClose={onClose}>
        <div className="pb-4">
          <div className="flex items-center gap-2 mb-4">
            <span className="material-symbols-outlined text-amber-500">account_balance</span>
            <h3 className="text-xl font-bold tracking-tight text-text-main dark:text-white">
              Transfer Manual
            </h3>
          </div>

          {/* Bank Account Info */}
          <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-4 mb-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-text-muted">Bank</span>
              <span className="font-bold dark:text-white">{bankConfig.bankName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-text-muted">No. Rekening</span>
              <span className="font-bold dark:text-white font-mono">{bankConfig.accountNumber}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-text-muted">Atas Nama</span>
              <span className="font-bold dark:text-white">{bankConfig.accountHolderName}</span>
            </div>
            <div className="flex justify-between border-t border-gray-200 dark:border-white/10 pt-2 mt-2">
              <span className="text-gray-500 dark:text-text-muted text-sm">Jumlah Transfer</span>
              <span className="text-lg font-bold text-primary">{formatCurrency(batch.batchFee)}</span>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 mb-4">
            <p className="text-xs text-blue-800 dark:text-blue-300">
              <strong>Catatan:</strong> Transfer harus sesuai nominal di atas. Setelah transfer, isi form di bawah ini.
            </p>
          </div>

          {/* Upload Proof Form */}
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-text-main dark:text-white">Upload Bukti Pembayaran</h4>

            <div>
              <label className="block text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">
                Bank Pengirim *
              </label>
              <input
                type="text"
                value={proofBankName}
                onChange={(e) => setProofBankName(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-black/20 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary"
                placeholder="Contoh: BCA, Mandiri"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">
                No. Rekening Pengirim *
              </label>
              <input
                type="text"
                value={proofAccountNumber}
                onChange={(e) => setProofAccountNumber(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-black/20 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary"
                placeholder="Nomor rekening Anda"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">
                Atas Nama Pengirim *
              </label>
              <input
                type="text"
                value={proofAccountHolder}
                onChange={(e) => setProofAccountHolder(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-black/20 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary"
                placeholder="Nama pemilik rekening"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">
                Bukti Transfer *
              </label>
              <FileUpload
                type="image"
                accept="image/*,.pdf"
                onUpload={(url: string) => setProofFileUrl(url)}
                label="Upload bukti transfer (image/PDF)"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">
                Catatan (Opsional)
              </label>
              <textarea
                value={proofNotes}
                onChange={(e) => setProofNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2.5 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-black/20 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary resize-none"
                placeholder="Catatan tambahan"
              />
            </div>

            {proofError && (
              <div className="bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 text-xs p-3 rounded-lg border border-red-200 dark:border-red-500/20">
                {proofError}
              </div>
            )}

            <button
              onClick={handleSubmitProof}
              disabled={isSubmittingProof}
              className={`w-full py-3.5 font-bold rounded-full shadow-lg transition-all flex items-center justify-center gap-2 mt-2 ${
                isSubmittingProof
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-primary text-black hover:shadow-primary/30 active:scale-[0.98]'
              }`}
            >
              {isSubmittingProof ? (
                <>
                  <div className="size-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  Mengirim...
                </>
              ) : (
                <>
                  Kirim Bukti
                  <span className="material-symbols-outlined text-lg">send</span>
                </>
              )}
            </button>
          </div>
        </div>
      </ModalBase>
    )
  }

  // Langkah: Berhasil
  if (step === 'success') {
    return (
      <ModalBase isOpen={isOpen} onClose={onClose}>
        <div className="flex flex-col items-center text-center pt-8 pb-4">
          <div className="size-24 bg-primary/20 rounded-full flex items-center justify-center mb-6 text-primary">
            <span className="material-symbols-outlined text-[48px] filled">check_circle</span>
          </div>
          <h3 className="text-2xl font-bold text-text-main dark:text-white mb-2">
            Pembayaran Berhasil!
          </h3>
          <p className="text-gray-500 dark:text-text-muted mb-8">
            Anda telah berhasil mendaftar ke batch <span className="font-semibold">{batch.title}</span> pada kursus {courseName}
          </p>

          <div className="w-full bg-gray-50 dark:bg-white/5 rounded-lg p-4 mb-8 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-text-muted">Batch</span>
              <span className="font-medium dark:text-white">{batch.title}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-text-muted">Total Dibayar</span>
              <span className="font-bold text-primary">{formatCurrency(batch.batchFee)}</span>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-full py-4 bg-primary text-black font-bold rounded-full mb-3 shadow-lg active:scale-[0.98] transition-transform"
          >
            Mulai Belajar
          </button>
        </div>
      </ModalBase>
    )
  }

  // Langkah: Pilih metode pembayaran
  if (step === 'select_payment') {
    return (
      <ModalBase isOpen={isOpen} onClose={onClose}>
        <div className="pb-4">
          <button
            onClick={() => setStep('review')}
            className="flex items-center gap-1 text-gray-500 dark:text-text-muted mb-4 hover:text-text-main transition-colors"
          >
            <span className="material-symbols-outlined text-lg">arrow_back</span>
            <span className="text-sm">Kembali</span>
          </button>

          <h3 className="text-xl font-bold tracking-tight text-text-main dark:text-white mb-6">
            Pilih Metode Pembayaran
          </h3>

          <div className="space-y-6 mb-8 max-h-96 overflow-y-auto">
            {/* Manual Transfer Option */}
            <div>
              <h4 className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-3">
                Transfer Manual
              </h4>
              <div
                onClick={handleManualTransfer}
                className="flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🏦</span>
                  <div>
                    <p className="font-bold text-sm dark:text-white">Transfer Bank</p>
                    <p className="text-xs text-gray-500 dark:text-text-muted">Transfer langsung ke rekening kami, lalu upload bukti</p>
                  </div>
                </div>
                <span className="material-symbols-outlined text-gray-400">arrow_forward</span>
              </div>
            </div>

            {Object.entries(paymentMethodGroups).map(([method, channels]) => (
              <div key={method}>
                <h4 className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-3">
                  {formatPaymentMethodLabel(method as keyof typeof paymentMethodGroups)}
                </h4>
                <div className="space-y-2">
                  {channels.map((channelInfo) => (
                    <div
                      key={channelInfo.channel}
                      onClick={() => setSelectedChannel(channelInfo.channel)}
                      className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all ${
                        selectedChannel === channelInfo.channel
                          ? 'border-2 border-primary bg-primary/5'
                          : 'border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{channelInfo.icon}</span>
                        <div>
                          <p className="font-bold text-sm dark:text-white">{channelInfo.name}</p>
                          <p className="text-xs text-gray-500 dark:text-text-muted">{channelInfo.description}</p>
                        </div>
                      </div>
                      <div className={`w-4 h-4 rounded-full ${
                        selectedChannel === channelInfo.channel
                          ? 'border-4 border-primary bg-white'
                          : 'border border-gray-300'
                      }`} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Ringkasan Pesanan */}
          <div className="border-t border-gray-100 dark:border-white/10 pt-4 mb-6 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-text-muted">Batch</span>
              <span className="dark:text-white">{batch.title}</span>
            </div>
            <div className="flex justify-between text-lg font-bold mt-2">
              <span className="dark:text-white">Total</span>
              <span className="text-primary">{formatCurrency(batch.batchFee)}</span>
            </div>
          </div>

          <button
            onClick={handleCreatePayment}
            disabled={!selectedChannel}
            className={`w-full py-4 font-bold rounded-full shadow-lg transition-all flex items-center justify-center gap-2 ${
              selectedChannel
                ? 'bg-primary text-black hover:shadow-primary/30 hover:scale-[1.01] active:scale-[0.98]'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <span>Bayar {formatCurrency(batch.batchFee)}</span>
            <span className="material-symbols-outlined text-lg">arrow_forward</span>
          </button>
        </div>
      </ModalBase>
    )
  }

  // Langkah: Ringkasan (default)
  return (
    <ModalBase isOpen={isOpen} onClose={onClose}>
      <h3 className="text-xl font-bold tracking-tight text-text-main dark:text-white mb-6">
        Checkout Batch
      </h3>

      {/* Info Kursus & Batch */}
      <div className="flex flex-col gap-3 p-4 bg-white dark:bg-black/20 rounded-lg border border-gray-100 dark:border-white/5 mb-6">
        <div>
          <p className="text-xs font-bold text-primary uppercase mb-1">Kursus</p>
          <h4 className="font-bold leading-tight dark:text-white">{courseName}</h4>
        </div>
        <div className="border-t border-gray-100 dark:border-white/10 pt-3">
          <p className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase mb-1">Batch</p>
          <p className="font-semibold dark:text-white">{batch.title}</p>
        </div>
      </div>

      {/* Ringkasan Biaya */}
      <div className="border-t border-gray-100 dark:border-white/10 pt-4 mb-8 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500 dark:text-text-muted">Biaya Batch</span>
          <span className="font-bold dark:text-white">{formatCurrency(batch.batchFee)}</span>
        </div>
        <div className="flex justify-between text-lg font-bold mt-4">
          <span className="dark:text-white">Total</span>
          <span className="dark:text-white">{formatCurrency(batch.batchFee)}</span>
        </div>
      </div>

      <button
        onClick={() => setStep('select_payment')}
        className="w-full py-4 bg-primary text-black font-bold rounded-full shadow-lg hover:shadow-primary/30 hover:scale-[1.01] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
      >
        <span>Pilih Metode Pembayaran</span>
        <span className="material-symbols-outlined text-lg">arrow_forward</span>
      </button>
    </ModalBase>
  )
}

export default BatchCheckoutModal
